import Navbar from "../components/Navbar";
import PromotionalCarousel from "../components/PromotionsCarousel";
import DynamicSpotlightBanner from "../components/DynamicSpotlightBanner";
import PromotionalCarousel2 from "../components/PromotionsCarousel2";
import PromotionalCarousel4 from "../components/PromotionalCarousel4";
import PromotionalCarousel3 from "../components/PromotionsCarousel3";
import LandingAboutSection from "../components/LandingAboutSection";
import LandingHighlightsSection from "../components/LandingHighlightsSection";
import LandingImageCarouselSection from "../components/LandingImageCarouselSection";
import LandingTestimonialsSection from "../components/LandingTestimonialsSection";
import LandingContactSection from "../components/LandingContactSection";
import LandingCustomSection from "../components/LandingCustomSection";
import Footer from "../components/Footer";

export const SEGMENT_TYPES = Object.freeze({
  NAVBAR: "navbar",
  HERO: "hero",
  SPOTLIGHT: "spotlight",
  FEATURED: "featured",
  BRANDS: "brands",
  CATEGORIES: "categories",
  ABOUT: "about",
  HIGHLIGHTS: "highlights",
  IMAGE_CAROUSEL: "imageCarousel",
  TESTIMONIALS: "testimonials",
  CONTACT: "contact",
  CUSTOM: "custom",
  FOOTER: "footer",
});

export const SEGMENT_SLOTS = Object.freeze({
  SHELL_HEADER: "shell-header",
  HOME: "home",
  SHELL_FOOTER: "shell-footer",
});

const EMPTY_PROPS = Object.freeze({});
const CATALOG_PARENT_VISIBILITY = Object.freeze(["sections.showProducts"]);

function defineSegment(type, getComponent, options = {}) {
  const parentVisibilityPaths = Array.isArray(options.parentVisibilityPaths)
    ? Object.freeze([...options.parentVisibilityPaths])
    : Object.freeze([]);

  return Object.freeze({
    id: type,
    type,
    get component() {
      return getComponent();
    },
    slot: options.slot || SEGMENT_SLOTS.HOME,
    visibilityPath: options.visibilityPath || null,
    parentVisibilityPaths,
    defaultVisible: options.defaultVisible !== false,
    requiresProducts: options.requiresProducts === true,
    visualStyleSection: options.visualStyleSection || type,
    wrapVisualStyle: options.wrapVisualStyle !== false,
    defaultProps: options.defaultProps
      ? Object.freeze({ ...options.defaultProps })
      : EMPTY_PROPS,
  });
}

const registry = {
  [SEGMENT_TYPES.NAVBAR]: defineSegment(SEGMENT_TYPES.NAVBAR, () => Navbar, {
    slot: SEGMENT_SLOTS.SHELL_HEADER,
    visibilityPath: "sections.showNavbar",
    wrapVisualStyle: false,
  }),
  [SEGMENT_TYPES.HERO]: defineSegment(SEGMENT_TYPES.HERO, () => PromotionalCarousel, {
    visibilityPath: "sections.showHero",
  }),
  [SEGMENT_TYPES.SPOTLIGHT]: defineSegment(
    SEGMENT_TYPES.SPOTLIGHT,
    () => DynamicSpotlightBanner,
    {
      visibilityPath: "sections.showSpotlight",
      parentVisibilityPaths: CATALOG_PARENT_VISIBILITY,
      requiresProducts: true,
    }
  ),
  [SEGMENT_TYPES.FEATURED]: defineSegment(
    SEGMENT_TYPES.FEATURED,
    () => PromotionalCarousel2,
    {
      visibilityPath: "sections.showFeatured",
      parentVisibilityPaths: CATALOG_PARENT_VISIBILITY,
      requiresProducts: true,
    }
  ),
  [SEGMENT_TYPES.BRANDS]: defineSegment(SEGMENT_TYPES.BRANDS, () => PromotionalCarousel4, {
    visibilityPath: "sections.showBrands",
    parentVisibilityPaths: CATALOG_PARENT_VISIBILITY,
    requiresProducts: true,
  }),
  [SEGMENT_TYPES.CATEGORIES]: defineSegment(
    SEGMENT_TYPES.CATEGORIES,
    () => PromotionalCarousel3,
    {
      visibilityPath: "sections.showCategories",
      parentVisibilityPaths: CATALOG_PARENT_VISIBILITY,
      requiresProducts: true,
    }
  ),
  [SEGMENT_TYPES.ABOUT]: defineSegment(SEGMENT_TYPES.ABOUT, () => LandingAboutSection, {
    visibilityPath: "sections.showAbout",
  }),
  [SEGMENT_TYPES.HIGHLIGHTS]: defineSegment(
    SEGMENT_TYPES.HIGHLIGHTS,
    () => LandingHighlightsSection,
    {
      visibilityPath: "sections.showHighlights",
    }
  ),
  [SEGMENT_TYPES.IMAGE_CAROUSEL]: defineSegment(
    SEGMENT_TYPES.IMAGE_CAROUSEL,
    () => LandingImageCarouselSection,
    {
      visibilityPath: "sections.showImageCarousel",
    }
  ),
  [SEGMENT_TYPES.TESTIMONIALS]: defineSegment(
    SEGMENT_TYPES.TESTIMONIALS,
    () => LandingTestimonialsSection,
    {
      visibilityPath: "sections.showTestimonials",
    }
  ),
  [SEGMENT_TYPES.CONTACT]: defineSegment(
    SEGMENT_TYPES.CONTACT,
    () => LandingContactSection,
    {
      visibilityPath: "sections.showContact",
    }
  ),
  [SEGMENT_TYPES.CUSTOM]: defineSegment(SEGMENT_TYPES.CUSTOM, () => LandingCustomSection),
  [SEGMENT_TYPES.FOOTER]: defineSegment(SEGMENT_TYPES.FOOTER, () => Footer, {
    slot: SEGMENT_SLOTS.SHELL_FOOTER,
    visibilityPath: "sections.showFooter",
    wrapVisualStyle: false,
  }),
};

export const SEGMENT_REGISTRY = Object.freeze(registry);
export const PUBLIC_SEGMENT_REGISTRY = SEGMENT_REGISTRY;

export const SEGMENT_ORDER = Object.freeze([
  SEGMENT_TYPES.NAVBAR,
  SEGMENT_TYPES.HERO,
  SEGMENT_TYPES.SPOTLIGHT,
  SEGMENT_TYPES.FEATURED,
  SEGMENT_TYPES.BRANDS,
  SEGMENT_TYPES.CATEGORIES,
  SEGMENT_TYPES.ABOUT,
  SEGMENT_TYPES.HIGHLIGHTS,
  SEGMENT_TYPES.IMAGE_CAROUSEL,
  SEGMENT_TYPES.TESTIMONIALS,
  SEGMENT_TYPES.CONTACT,
  SEGMENT_TYPES.FOOTER,
]);

export const RENDERABLE_SEGMENT_TYPES = Object.freeze([...SEGMENT_ORDER, SEGMENT_TYPES.CUSTOM]);

const segmentComponents = {};
for (const type of RENDERABLE_SEGMENT_TYPES) {
  Object.defineProperty(segmentComponents, type, {
    enumerable: true,
    get() {
      return SEGMENT_REGISTRY[type].component;
    },
  });
}
export const SEGMENT_COMPONENTS = Object.freeze(segmentComponents);

export function getSegmentDefinition(segmentType) {
  return SEGMENT_REGISTRY[segmentType] || null;
}

export function getSegmentComponent(segmentType) {
  return getSegmentDefinition(segmentType)?.component || null;
}

export function getSegmentDefaultProps(segmentType) {
  const defaultProps = getSegmentDefinition(segmentType)?.defaultProps;
  return defaultProps ? { ...defaultProps } : {};
}

export function getRenderableSegments(slot) {
  return RENDERABLE_SEGMENT_TYPES.map((type) => SEGMENT_REGISTRY[type]).filter(
    (definition) => !slot || definition.slot === slot
  );
}

export function isRenderableSegmentType(segmentType) {
  return Boolean(getSegmentComponent(segmentType));
}

export default SEGMENT_REGISTRY;
